import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { titleCase } from "../lib/utils";
import {
  ArrowLeft,
  Rocket,
  Clock,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Pencil,
  Trash2,
  Send,
  GitBranch,
  FileText,
  AlertTriangle,
  RotateCcw,
  Link2,
  Star,
} from "lucide-react";
import {
  deploymentsApi,
  enhancedDeploymentsApi,
  deploymentRequestsApi,
  environmentsApi,
  EnhancedDeployment,
  DeploymentStatus,
  Approval,
} from "../api/client";
import { formatRelativeTime, formatDate } from "../lib/utils";
import ConfirmationModal from "../components/ConfirmationModal";
import ReleaseNotesTab from "../components/ReleaseNotesTab";

const statusConfig: Record<
  DeploymentStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  draft: {
    label: "Draft",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-700",
  },
  pending_approval: {
    label: "Pending Approval",
    icon: <Clock className="w-4 h-4" />,
    className: "bg-yellow-100 text-yellow-700",
  },
  approved: {
    label: "Approved",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-blue-100 text-blue-700",
  },
  rejected: {
    label: "Rejected",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
  deploying: {
    label: "Deploying",
    icon: <PlayCircle className="w-4 h-4" />,
    className: "bg-purple-100 text-purple-700",
  },
  completed: {
    label: "Completed",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  success: {
    label: "Success",
    icon: <CheckCircle2 className="w-4 h-4" />,
    className: "bg-green-100 text-green-700",
  },
  failed: {
    label: "Failed",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-red-100 text-red-700",
  },
  cancelled: {
    label: "Cancelled",
    icon: <XCircle className="w-4 h-4" />,
    className: "bg-gray-100 text-gray-500",
  },
};

type TabType = "overview" | "release-notes";

export default function DeploymentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [showEditDeploymentModal, setShowEditDeploymentModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    variant: "danger" | "warning" | "info";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    confirmLabel: "Confirm",
    variant: "warning",
    onConfirm: () => {},
  });

  const { data: deploymentData, isLoading: deploymentLoading } = useQuery({
    queryKey: ["deployment", id],
    queryFn: () => deploymentsApi.get(id!),
    enabled: !!id,
  });

  const deployment = deploymentData?.deployment;

  // Cast to EnhancedDeployment to check for multi-service deployment
  const enhancedDeploymentCheck = deployment as EnhancedDeployment | undefined;
  const isMultiServiceDeployment =
    enhancedDeploymentCheck?.deployment_group_id ||
    enhancedDeploymentCheck?.linked_deployment_id ||
    (enhancedDeploymentCheck?.is_primary &&
      enhancedDeploymentCheck?.linked_deployments &&
      enhancedDeploymentCheck.linked_deployments.length > 0);

  // Fetch with linked data for multi-service deployments
  const { data: deploymentWithLinked } = useQuery({
    queryKey: ["deployment-with-linked", id],
    queryFn: () => enhancedDeploymentsApi.getWithLinked(id!),
    enabled: !!id && !!isMultiServiceDeployment,
  });

  // Fetch environment to check if it's production
  const { data: environment } = useQuery({
    queryKey: ["environment", deployment?.environment_id],
    queryFn: () => environmentsApi.get(deployment!.environment_id),
    enabled: !!deployment?.environment_id,
  });
  const isProductionEnv = environment?.is_production ?? false;

  // Cast deployment to EnhancedDeployment to access Phase 6 fields
  const enhancedDeployment = deployment as EnhancedDeployment | undefined;
  const currentStatus: DeploymentStatus =
    enhancedDeployment?.enhanced_status ||
    enhancedDeployment?.status ||
    "draft";

  // Fetch deployment request and approvals if we have a deployment_request_id
  const { data: approvalData, isError: approvalError } = useQuery({
    queryKey: ["deployment-request", enhancedDeployment?.deployment_request_id],
    queryFn: () =>
      deploymentRequestsApi.get(enhancedDeployment!.deployment_request_id!),
    enabled: !!enhancedDeployment?.deployment_request_id,
    retry: false, // Don't retry on failure - the request might not exist
  });

  const approvals = approvalData?.approvals || [];
  const hasApprovalData = !approvalError && approvals.length > 0;
  // Check if any approver has already approved (blocks reset to draft)
  const hasAnyApproval = approvals.some(
    (a: Approval) => a.decision === "approved",
  );

  const updateStatusMutation = useMutation({
    mutationKey: ["updateDeploymentStatus"],
    mutationFn: (status: DeploymentStatus) =>
      enhancedDeploymentsApi.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployment", id] });
      queryClient.invalidateQueries({ queryKey: ["deployments"] });
    },
  });

  const submitMutation = useMutation({
    mutationKey: ["submitDeployment"],
    mutationFn: () => enhancedDeploymentsApi.submit(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deployment", id] });
      queryClient.invalidateQueries({ queryKey: ["deployments"] });
      // Also invalidate release cycle deployments if this deployment is part of a release cycle
      if (enhancedDeployment?.release_cycle_id) {
        queryClient.invalidateQueries({
          queryKey: [
            "release-cycle-deployments",
            enhancedDeployment.release_cycle_id,
          ],
        });
      }
    },
  });

  const deleteDeploymentMutation = useMutation({
    mutationKey: ["deleteDeployment"],
    mutationFn: async () => {
      // Capture release_cycle_id before deletion since it won't be available after
      const releaseCycleId = enhancedDeployment?.release_cycle_id;
      await enhancedDeploymentsApi.delete(id!);
      return { releaseCycleId };
    },
    onSuccess: async (result) => {
      // Invalidate queries and wait for them to complete before navigating
      await queryClient.invalidateQueries({ queryKey: ["deployments"] });
      if (result.releaseCycleId) {
        await queryClient.invalidateQueries({
          queryKey: ["release-cycle-deployments", result.releaseCycleId],
        });
        // Also invalidate the release cycle itself in case it shows deployment count
        await queryClient.invalidateQueries({
          queryKey: ["release-cycle", result.releaseCycleId],
        });
        navigate(`/release-cycles/${result.releaseCycleId}`);
      } else {
        navigate("/deployments");
      }
    },
  });

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      confirmLabel?: string;
      variant?: "danger" | "warning" | "info";
    },
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmLabel: options?.confirmLabel || "Confirm",
      variant: options?.variant || "warning",
      onConfirm,
    });
  };

  const handleDeleteDeployment = () => {
    if (currentStatus !== "draft") {
      showConfirm(
        "Cannot Delete Deployment",
        "Only draft deployments can be deleted. This deployment has already progressed past the draft stage.",
        () => closeConfirmModal(),
        { confirmLabel: "OK", variant: "info" },
      );
      return;
    }
    showConfirm(
      "Delete Deployment",
      "Are you sure you want to delete this deployment? This action cannot be undone.",
      () => {
        deleteDeploymentMutation.mutate();
        closeConfirmModal();
      },
      { confirmLabel: "Delete", variant: "danger" },
    );
  };

  if (deploymentLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!deployment) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">
            Deployment not found
          </h2>
          <button
            onClick={() => navigate("/deployments")}
            className="btn btn-primary mt-4"
          >
            Back to Deployments
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = statusConfig[currentStatus];
  const isDraft = currentStatus === "draft";
  const isRejected = currentStatus === "rejected";
  const canEdit = isDraft || isRejected; // Allow editing in draft or rejected state
  const canSubmit = isDraft || isRejected; // Allow resubmitting after rejection

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => {
            if (enhancedDeployment?.release_cycle_id) {
              navigate(
                `/release-cycles/${enhancedDeployment.release_cycle_id}`,
              );
            } else {
              navigate("/deployments");
            }
          }}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {enhancedDeployment?.release_cycle_id
            ? "Back to Release Cycle"
            : "Back to Deployments"}
        </button>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <Rocket className="w-8 h-8 text-brand-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {enhancedDeployment?.title ||
                    `${titleCase(deployment.service_name || "")} - ${deployment.environment_name}`}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 font-mono">
                    {deployment.version}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusInfo.className}`}
                  >
                    {statusInfo.icon}
                    {statusInfo.label}
                  </span>
                  {enhancedDeployment?.sequence_type && !isProductionEnv && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 uppercase">
                      {enhancedDeployment.sequence_type}
                      {enhancedDeployment.sequence_number &&
                        ` #${enhancedDeployment.sequence_number}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
              <span>
                <strong>Service:</strong>{" "}
                {titleCase(deployment.service_name || "")}
              </span>
              <span>
                <strong>Environment:</strong> {deployment.environment_name}
              </span>
              {enhancedDeployment?.release_cycle_id && (
                <span
                  className="flex items-center gap-1 text-brand-600 hover:text-brand-700 cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/release-cycles/${enhancedDeployment.release_cycle_id}`,
                    )
                  }
                >
                  <GitBranch className="w-4 h-4" />
                  {enhancedDeployment.release_cycle_title ||
                    "View Release Cycle"}
                </span>
              )}
              <span>Created {formatRelativeTime(deployment.created_at)}</span>
            </div>
            {enhancedDeployment?.description && (
              <p className="text-gray-600 mt-2">
                {enhancedDeployment.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            {canSubmit && (
              <button
                onClick={() => submitMutation.mutate()}
                disabled={submitMutation.isPending}
                className="btn btn-primary"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit for Approval
              </button>
            )}

            {/* Reset to Draft - only if pending_approval and no one has approved yet */}
            {currentStatus === "pending_approval" &&
              hasApprovalData &&
              !hasAnyApproval && (
                <button
                  onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      title: "Reset to Draft",
                      message:
                        "This will reset the deployment back to draft status. You will need to submit it for approval again. Continue?",
                      confirmLabel: "Reset to Draft",
                      variant: "warning",
                      onConfirm: () => updateStatusMutation.mutate("draft"),
                    });
                  }}
                  disabled={updateStatusMutation.isPending}
                  className="btn btn-secondary"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset to Draft
                </button>
              )}

            {currentStatus === "deploying" && (
              <button
                onClick={() => updateStatusMutation.mutate("completed")}
                disabled={updateStatusMutation.isPending}
                className="btn btn-primary"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark Complete
              </button>
            )}
            {isDraft && (
              <>
                <button
                  onClick={() => setShowEditDeploymentModal(true)}
                  className="btn btn-secondary"
                  title="Edit deployment"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDeleteDeployment}
                  disabled={deleteDeploymentMutation.isPending}
                  className="btn btn-secondary text-red-600 hover:text-red-700"
                  title="Delete deployment"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Secondary Deployment Notice - Show prominently at top if this is a secondary deployment */}
      {enhancedDeployment?.linked_deployment_id &&
        !enhancedDeployment?.is_primary && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Link2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">
                  Part of Multi-Service Deployment
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  This is a secondary service in a multi-service deployment.
                  View the{" "}
                  <Link
                    to={`/deployments/${enhancedDeployment.linked_deployment_id}`}
                    className="font-medium underline hover:text-blue-900"
                  >
                    primary deployment
                  </Link>{" "}
                  for the complete deployment details and approval workflow.
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-8">
          {[
            { id: "overview", label: "Overview", icon: Rocket },
            { id: "release-notes", label: "Release Notes", icon: FileText },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 py-3 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-brand-600 text-brand-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deployment Info Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Deployment Information
            </h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-gray-500">Service</dt>
                <dd className="text-gray-900 font-medium flex items-center gap-2">
                  {titleCase(deployment.service_name || "")}
                  {enhancedDeployment?.is_primary &&
                    (deploymentWithLinked?.linked_deployments?.length ?? 0) >
                      0 && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                        <Star className="w-3 h-3" />
                        Primary
                      </span>
                    )}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Environment</dt>
                <dd className="text-gray-900 font-medium">
                  {deployment.environment_name}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Version</dt>
                <dd className="font-mono bg-gray-100 px-2 py-0.5 rounded text-sm">
                  {deployment.version}
                </dd>
              </div>
              {deployment.previous_version && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Previous Version</dt>
                  <dd className="font-mono bg-gray-100 px-2 py-0.5 rounded text-sm">
                    {deployment.previous_version}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusInfo.className}`}
                  >
                    {statusInfo.label}
                  </span>
                </dd>
              </div>
              {enhancedDeployment?.sequence_type && !isProductionEnv && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Sequence</dt>
                  <dd className="text-gray-900 uppercase">
                    {enhancedDeployment.sequence_type}
                    {enhancedDeployment.sequence_number &&
                      ` #${enhancedDeployment.sequence_number}`}
                  </dd>
                </div>
              )}
              <div className="flex justify-between">
                <dt className="text-gray-500">Created</dt>
                <dd className="text-gray-900">
                  {formatDate(deployment.created_at)}
                </dd>
              </div>
              {enhancedDeployment?.created_by_name && (
                <div className="flex justify-between">
                  <dt className="text-gray-500">Created By</dt>
                  <dd className="text-gray-900">
                    {enhancedDeployment.created_by_name}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Linked Services Card - Only show for primary multi-service deployments */}
          {deploymentWithLinked?.linked_deployments &&
            deploymentWithLinked.linked_deployments.length > 0 &&
            enhancedDeployment?.is_primary && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Link2 className="w-5 h-5 text-brand-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Linked Services
                  </h3>
                  <span className="text-sm text-gray-500">
                    ({deploymentWithLinked.linked_deployments.length + 1} total)
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  This deployment is part of a multi-service deployment group.
                  All services share the same approval and will be deployed
                  together.
                </p>
                <div className="space-y-2">
                  {/* Primary service (current deployment) */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-gray-900">
                        {titleCase(deployment.service_name || "")}
                      </span>
                      <span className="text-xs text-amber-700 font-medium">
                        Primary
                      </span>
                    </div>
                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                      {deployment.version}
                    </span>
                  </div>

                  {/* Linked services */}
                  {deploymentWithLinked.linked_deployments.map(
                    (linked: EnhancedDeployment) => (
                      <div
                        key={linked.id}
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => navigate(`/deployments/${linked.id}`)}
                      >
                        <div className="flex items-center gap-2">
                          <Link2 className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {titleCase(linked.service_name || "")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {linked.version}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig[linked.enhanced_status || linked.status || "draft"].className}`}
                          >
                            {
                              statusConfig[
                                linked.enhanced_status ||
                                  linked.status ||
                                  "draft"
                              ].label
                            }
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}

          {/* Deployment Status Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Deployment Status
            </h3>
            <div className="flex flex-col items-center justify-center py-4">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-medium ${statusInfo.className}`}
              >
                {statusInfo.icon}
                {statusInfo.label}
              </div>
              <p className="text-gray-500 text-sm mt-3 text-center">
                {currentStatus === "draft" &&
                  "This deployment is in draft mode. Add release notes and submit for approval."}
                {currentStatus === "pending_approval" &&
                  "Waiting for approval before deployment can begin."}
                {currentStatus === "approved" &&
                  "Approved and ready to deploy."}
                {currentStatus === "deploying" &&
                  "Deployment is currently in progress."}
                {currentStatus === "completed" &&
                  "Deployment completed successfully."}
                {currentStatus === "failed" &&
                  "Deployment failed. Check logs for details."}
                {currentStatus === "cancelled" &&
                  "This deployment was cancelled."}
              </p>
            </div>

            {/* Warning for pending approval with no approvers */}
            {currentStatus === "pending_approval" && !hasApprovalData && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">
                      No approvers configured
                    </p>
                    <p className="text-xs text-amber-700 mt-1">
                      This deployment is pending approval but no approvers were
                      configured for this environment. Please configure
                      approvers in environment settings, then reset to draft and
                      resubmit.
                    </p>
                    <button
                      onClick={() => updateStatusMutation.mutate("draft")}
                      disabled={updateStatusMutation.isPending}
                      className="mt-2 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300"
                    >
                      Reset to Draft
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Approval Status - Show when there are approvals */}
            {hasApprovalData && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Approval Status
                </h4>
                <div className="space-y-2">
                  {approvals.map((approval: Approval) => (
                    <div
                      key={approval.id}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        {approval.decision === "approved" ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : approval.decision === "rejected" ? (
                          <XCircle className="w-4 h-4 text-red-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-600" />
                        )}
                        <div>
                          <span className="text-sm font-medium text-gray-900">
                            {approval.approver_name || approval.approver_email}
                          </span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({approval.role})
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        {approval.decision === "approved" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            Approved
                          </span>
                        ) : approval.decision === "rejected" ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            Rejected
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                            Pending
                          </span>
                        )}
                        {approval.decided_at && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {formatRelativeTime(approval.decided_at)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notes Card */}
          {deployment.notes && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Notes
              </h3>
              <p className="text-gray-600 whitespace-pre-wrap">
                {deployment.notes}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "release-notes" && (
        <ReleaseNotesTab deploymentId={id!} canEdit={canEdit} />
      )}

      {/* Edit Deployment Modal */}
      {showEditDeploymentModal && enhancedDeployment && (
        <EditDeploymentModal
          deployment={enhancedDeployment}
          isProductionEnv={isProductionEnv}
          onClose={() => setShowEditDeploymentModal(false)}
          onUpdated={() => {
            setShowEditDeploymentModal(false);
            queryClient.invalidateQueries({ queryKey: ["deployment", id] });
          }}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        variant={confirmModal.variant}
        isLoading={deleteDeploymentMutation.isPending}
      />
    </div>
  );
}

interface EditDeploymentModalProps {
  deployment: EnhancedDeployment;
  isProductionEnv: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

function EditDeploymentModal({
  deployment,
  isProductionEnv,
  onClose,
  onUpdated,
}: EditDeploymentModalProps) {
  const [formData, setFormData] = useState({
    version: deployment.version,
    title: deployment.title || "",
    description: deployment.description || "",
    sequence_type: (deployment.sequence_type || "rc") as
      | "rc"
      | "hotfix"
      | "rollup",
    sequence_number: deployment.sequence_number || 1,
    notes: deployment.notes || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      await enhancedDeploymentsApi.update(deployment.id, {
        version: formData.version,
        title: formData.title || undefined,
        description: formData.description || undefined,
        sequence_type: formData.sequence_type,
        sequence_number: formData.sequence_number,
        notes: formData.notes || undefined,
      });
      onUpdated();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update deployment",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Deployment
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {titleCase(deployment.service_name || "")} →{" "}
            {deployment.environment_name}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Version <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) =>
                  setFormData({ ...formData, version: e.target.value })
                }
                placeholder="e.g., 3.14.0"
                className="input w-full"
                required
              />
            </div>

            {/* Only show sequence fields for non-production environments */}
            {!isProductionEnv && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sequence Type
                  </label>
                  <select
                    value={formData.sequence_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sequence_type: e.target.value as
                          | "rc"
                          | "hotfix"
                          | "rollup",
                      })
                    }
                    className="input w-full"
                  >
                    <option value="rc">Release Candidate (RC)</option>
                    <option value="hotfix">Hotfix</option>
                    <option value="rollup">Rollup</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sequence Number
                  </label>
                  <input
                    type="number"
                    value={formData.sequence_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sequence_number: parseInt(e.target.value) || 1,
                      })
                    }
                    min={1}
                    className="input w-full"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Optional custom title"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Optional description..."
                rows={3}
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Optional notes..."
                rows={2}
                className="input w-full"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
